import { useMemo, useState } from "react";
import { progressPct } from "@/lib/progress";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Sparkles, AlertTriangle, Loader2, Plus, Link2, Printer, Wand2, Palette, RotateCcw, CheckCircle2, ClipboardList, Check, Users, BellRing, Repeat, History, Workflow, ChevronDown, ArrowRightLeft } from "lucide-react";
import { computeWbsIds } from "@/lib/wbsIds";
import { printFlow } from "@/lib/wbsExport";
import { workloadHeat } from "@/lib/heat";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SwatchPicker from "@/components/SwatchPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PersonCard } from "@/components/CardsView";
import api from "@/lib/api";

const FLOW_DEFAULTS = { input: "#1c5f9e", summary: "#1a73c9", task: "#a8cff0", milestone: "#1565c0", output: "#2f7fc1" };
const FLOW_LABELS = { input: "Input block", summary: "Summary bar", milestone: "Milestones", output: "Output block" };
const isDoneT = (t) => t.status === "done" || (t.percent_complete || 0) >= 100;

export const HEALTH_COLORS = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444" };
export const taskHealth = (t, todayStr) => {
  if (isDoneT(t)) return "green";
  if (t.track_status === "jeopardy" || t.status === "blocked" || (t.end_date && t.end_date < todayStr)) return "red";
  if (t.track_status === "at_risk") return "yellow";
  if (t.start_date && t.end_date) {
    const s = new Date(t.start_date), e = new Date(t.end_date), n = new Date();
    const total = e - s;
    if (total > 0 && n > s) {
      const expected = Math.min(100, ((n - s) / total) * 100);
      if ((t.percent_complete || 0) + 20 < expected) return "yellow";
    }
  }
  return "green";
};

const fmtD = (s) => (s ? new Date(s + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
const depIdOf = (d) => (typeof d === "string" ? d : d.predecessor_id);
const dateOf = (t) => t.end_date || t.start_date || "9999";

const GreenArrow = () => (
  <svg width="52" height="28" viewBox="0 0 52 28" className="shrink-0" aria-hidden="true">
    <polygon points="2,9 18,9 18,3 30,14 18,25 18,19 2,19" fill="#57bb63" />
    <polygon points="24,9 36,9 36,3 48,14 36,25 36,19 24,19" fill="#7ed087" />
  </svg>
);

export const PhaseFlowView = ({ projectId, projectName, phases, tasks, phaseColors, onPhaseColor, onOpenTask, onLinkInput, onCreateMilestone, fetchTips, onSetup, flowColors, onFlowColors, onTipToAction, autoNudgePhases, onReorderPhase, onSetFlowRole, phaseLabels, onPhaseLabel, progressStyle }) => {
  const C = { ...FLOW_DEFAULTS, ...(flowColors || {}) };
  const today = new Date().toISOString().slice(0, 10);
  const [resCard, setResCard] = useState(null);
  const [resPhase, setResPhase] = useState(null);
  const [resLoading, setResLoading] = useState(null);
  const [nudgingPhase, setNudgingPhase] = useState(null);
  const [mappingPhase, setMappingPhase] = useState(null);
  const [numPopover, setNumPopover] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");
  const [ioPicker, setIoPicker] = useState(null);
  const setRole = async (t, role) => {
    setIoPicker(null);
    try {
      await onSetFlowRole(t, role);
      toast.success(role ? `"${t.title}" is now this phase's ${role}` : `"${t.title}" is a regular item again`);
    } catch (e) { toast.error("Could not update the item"); }
  };
  const IoRolePicker = ({ t, light }) => (
    <Popover open={ioPicker === t.id} onOpenChange={(o) => setIoPicker(o ? t.id : null)}>
      <PopoverTrigger asChild>
        <button type="button"
          className={`shrink-0 p-1 rounded transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${light ? "text-white/80 hover:text-white hover:bg-white/20" : "text-muted-foreground hover:text-primary hover:bg-primary/10"}`}
          title="Mark this item as the phase's input or output" data-testid={`io-role-btn-${t.id}`}>
          <ArrowRightLeft className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1.5 space-y-0.5" data-testid={`io-role-pop-${t.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground px-2 pt-1 pb-1.5 truncate">"{t.title}" is…</p>
        {[["input", "the Input — needed to start"], ["output", "the Output — promised result"], ["", "a regular item"]].map(([v, label]) => (
          <button key={v || "none"} type="button" onClick={() => setRole(t, v)}
            className={`w-full text-left text-xs rounded px-2 py-1.5 font-semibold transition-colors ${(t.flow_role || "") === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-foreground"}`}
            data-testid={`io-role-opt-${t.id}-${v || "none"}`}>
            {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
  const nav = useNavigate();
  const mapPhase = async (ph) => {
    setMappingPhase(ph);
    try {
      const { data } = await api.post("/processes/ai-from-plan", { project_id: projectId, phase: ph, name: "" }, { timeout: 120000 });
      toast.success(`"${data.name}" mapped with ${data.steps.length} steps — opening the Process page`);
      nav(`/projects/${projectId}?tab=process`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Maestro couldn't map that phase — please try again");
    } finally { setMappingPhase(null); }
  };
  const [autoPhases, setAutoPhases] = useState(null);
  const effAuto = autoPhases ?? (autoNudgePhases || []);
  const toggleAutoNudge = async (ph) => {
    const enabled = !effAuto.includes(ph);
    try {
      const { data } = await api.post(`/projects/${projectId}/auto-nudge`, { phase: ph, enabled });
      setAutoPhases(data.auto_nudge_phases || []);
      toast.success(enabled ? `Auto-nudge on — "${ph}" resources get a Monday reminder until the output is delivered` : `Auto-nudge off for "${ph}"`);
    } catch (e) {
      toast.error("Could not update auto-nudge");
    }
  };
  const [history, setHistory] = useState(null);
  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/nudge-history`);
      setHistory(data);
    } catch (e) { setHistory([]); }
  };
  const nudgeAllPhase = async (ph, force) => {
    setNudgingPhase(ph);
    try {
      const { data } = await api.post("/cards/send", { phase: ph, project_id: projectId, force: force === true });
      const bits = [];
      if (data.skipped) bits.push(`${data.skipped} skipped (no email)`);
      if (data.recent_skipped) bits.push(`${data.recent_skipped} skipped (already nudged this week)`);
      const opts = data.recent_skipped
        ? { duration: 8000, action: { label: "Send anyway", onClick: () => nudgeAllPhase(ph, true) } }
        : undefined;
      if (data.sent) {
        toast.success(`Nudged ${data.sent} ${data.sent === 1 ? "person" : "people"} about their "${ph}" tasks${bits.length ? ` — ${bits.join(", ")}` : ""}`, opts);
      } else {
        toast.info(bits.length ? `Nothing sent — ${bits.join(", ")}` : "No one has open tasks in this phase", opts);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nudge failed");
    } finally {
      setNudgingPhase(null);
    }
  };
  const openResource = async (name, phase) => {
    setResLoading(name);
    try {
      const { data } = await api.get("/cards", { params: projectId ? { project_id: projectId } : {} });
      const card = (Array.isArray(data) ? data : data.cards || []).find((c) => (c.person || "").trim().toLowerCase() === name.trim().toLowerCase());
      if (card) { setResPhase(phase || null); setResCard(card); }
      else toast.info(`No resource card found for ${name} yet`);
    } catch {
      toast.error("Couldn't load the resource card");
    } finally { setResLoading(null); }
  };
  const [tipActions, setTipActions] = useState({});
  const tipToAction = async (key, tip, phase) => {
    if (!onTipToAction) return;
    setTipActions((s) => ({ ...s, [key]: "saving" }));
    try {
      await onTipToAction(tip, phase);
      setTipActions((s) => ({ ...s, [key]: "done" }));
    } catch {
      setTipActions((s) => ({ ...s, [key]: undefined }));
    }
  };
  const TipActionBtn = ({ k, tip, phase }) => {
    if (!onTipToAction) return null;
    if (tipActions[k] === "done") return <span className="shrink-0 inline-flex items-center gap-1 font-bold text-emerald-600" data-testid={`flow-tip-created-${k}`}><Check className="h-3 w-3" /> Added</span>;
    return (
      <button type="button" onClick={() => tipToAction(k, tip, phase)} disabled={tipActions[k] === "saving"}
        title="Turn this tip into an action item"
        className="shrink-0 inline-flex items-center gap-1 font-bold underline disabled:opacity-50" data-testid={`flow-tip-action-${k}`}>
        {tipActions[k] === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ClipboardList className="h-3 w-3" />} Action item
      </button>
    );
  };
  const wbsIds = useMemo(() => computeWbsIds(tasks, phases), [tasks, phases]);
  const byId = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t])), [tasks]);
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(false);

  const askMaestro = async () => {
    setLoading(true);
    try { setTips(await fetchTips()); toast.success("Maestro reviewed your flow"); }
    catch { toast.error("Maestro couldn't analyze the flow — please try again."); }
    finally { setLoading(false); }
  };

  const model = useMemo(() => phases.map((ph, pi) => {
    const items = tasks.filter((t) => t.phase === ph);
    const ioInputs = items.filter((t) => !t.is_summary && t.flow_role === "input").sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : 1));
    const ioOutputs = items.filter((t) => !t.is_summary && t.flow_role === "output").sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : 1));
    const rest = items.filter((t) => t.is_summary || !t.flow_role);
    const milestones = rest.filter((t) => t.is_milestone).sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : 1));
    const workTasks = rest.filter((t) => !t.is_milestone && !t.is_summary).sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : 1));
    const summary = items.find((t) => t.is_summary);
    const inputText = summary?.description?.startsWith("Input:") ? summary.description.slice(6).trim() : null;
    const inputs = [];
    items.forEach((t) => (t.dependencies || []).forEach((d) => {
      const p = byId[depIdOf(d)];
      if (p && p.phase !== ph && !inputs.some((x) => x.id === p.id) && !ioInputs.some((x) => x.id === p.id)) inputs.push(p);
    }));
    const output = ioOutputs[ioOutputs.length - 1] || milestones[milestones.length - 1] || null;
    const extraOutputs = ioOutputs.length > 1 ? ioOutputs.slice(0, -1) : [];
    const midMilestones = milestones.filter((m) => m.id !== output?.id);
    const delivery = output ? (output.end_date || output.start_date) : (workTasks.map((t) => t.end_date).filter(Boolean).sort().pop() || null);
    const scored = items.filter((t) => !t.is_summary);
    const pct = scored.length ? progressPct(scored, progressStyle) : 0;
    const todayStr = new Date().toISOString().slice(0, 10);
    const health = scored.some((t) => !isDoneT(t) && (t.track_status === "jeopardy" || (t.end_date && t.end_date < todayStr))) ? "jeopardy"
      : scored.some((t) => !isDoneT(t) && t.track_status === "at_risk") ? "at_risk" : "on_track";
    const resources = [...new Set(items.flatMap((t) => [t.assignee, t.responsible]).map((r) => (r || "").trim()).filter(Boolean))];
    const resCounts = {};
    items.filter((t) => !t.is_summary && !isDoneT(t)).forEach((t) => {
      [...new Set([t.assignee, t.responsible].map((r) => (r || "").trim()).filter(Boolean))].forEach((r) => { resCounts[r] = (resCounts[r] || 0) + 1; });
    });
    const maxRes = Math.max(1, ...Object.values(resCounts));
    const doneCount = scored.filter(isDoneT).length;
    return { ph, pi, items, workTasks, summary, inputText, midMilestones, inputs, ioInputs, extraOutputs, output, delivery, pct, health, resources, resCounts, maxRes, doneCount, scoredCount: scored.length };
  }), [phases, tasks, byId, progressStyle]);

  const inputCandidate = (m) => {
    if (m.pi === 0) return null;
    const prev = model[m.pi - 1];
    return prev.output || prev.workTasks[prev.workTasks.length - 1] || null;
  };
  const inputTarget = (m) => m.workTasks[0] || m.items[0] || null;

  return (
    <div className="space-y-6" data-testid="flow-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl space-y-1.5">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Input → Tasks → Output discipline:</span> every phase should consume an input (a link from earlier work) and promise an output (a milestone with a delivery date). Gaps are flagged below with one-click fixes.
          </p>
          <div className="flex items-center gap-3 flex-wrap" data-testid="flow-health-legend">
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Task colors:</span>
            {[["green", "On track / done"], ["yellow", "Behind / at risk"], ["red", "Overdue / jeopardy"]].map(([k, label]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground/70" data-testid={`flow-legend-${k}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_COLORS[k] }} /> {label}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onFlowColors && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="flow-colors-button">
                  <Palette className="h-3.5 w-3.5" /> Colors
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 space-y-2" data-testid="flow-colors-popover">
                <p className="text-xs font-bold">Flow block colors (all phases)</p>
                {Object.keys(FLOW_LABELS).map((k) => (
                  <div key={k} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">{FLOW_LABELS[k]}</span>
                    <SwatchPicker value={C[k]} onChange={(c) => onFlowColors({ ...(flowColors || {}), [k]: c })} title={FLOW_LABELS[k]} testid={`flow-color-${k}`} />
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">Task pills are colored by health: <span className="font-bold" style={{ color: HEALTH_COLORS.green }}>green</span> on track, <span className="font-bold" style={{ color: HEALTH_COLORS.yellow }}>yellow</span> behind/at risk, <span className="font-bold" style={{ color: HEALTH_COLORS.red }}>red</span> overdue/jeopardy. Use the palette on each phase for per-phase box colors.</p>
                <button type="button" onClick={() => onFlowColors({})} className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid="flow-colors-reset">
                  <RotateCcw className="h-3 w-3" /> Reset to defaults
                </button>
              </PopoverContent>
            </Popover>
          )}
          {onSetup && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={onSetup} data-testid="flow-io-setup">
              <Wand2 className="h-3.5 w-3.5 text-primary" /> IO Map builder
            </Button>
          )}
          {projectId && (
            <Popover onOpenChange={(v) => v && loadHistory()}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="flow-nudge-history-btn">
                  <History className="h-3.5 w-3.5" /> Nudges
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 max-h-80 overflow-y-auto space-y-1.5" data-testid="flow-nudge-history-pop">
                <p className="text-xs font-bold">Nudge history</p>
                {history === null && <p className="text-xs text-muted-foreground">Loading…</p>}
                {history !== null && history.length === 0 && <p className="text-xs text-muted-foreground italic">No nudges sent yet for this project.</p>}
                {(history || []).map((h) => (
                  <div key={h.id} className="flex items-center gap-2 text-xs border-b border-border/50 pb-1" data-testid={`nudge-log-${h.id}`}>
                    <BellRing className="h-3 w-3 text-primary shrink-0" />
                    <span className="font-semibold truncate">{h.person}</span>
                    <span className="text-muted-foreground truncate flex-1">{h.phase}</span>
                    {h.auto && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 rounded px-1 shrink-0">auto</span>}
                    <span className="text-muted-foreground shrink-0">{new Date(h.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => printFlow(projectName || "Project", phases, tasks, phaseColors, phaseLabels || {})} data-testid="flow-print">
            <Printer className="h-3.5 w-3.5" /> Print / PDF
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={askMaestro} disabled={loading} data-testid="flow-ask-maestro">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-amber-500" />} Ask Maestro
          </Button>
        </div>
      </div>

      {model.map((m) => {
        const color = phaseColors[m.ph] || "#1c6cb5";
        const PC = { ...C, ...((flowColors?.phases || {})[m.ph] || {}) };
        const setPhaseBox = (k, c) => {
          const cur = flowColors || {};
          const ph = { ...(cur.phases || {}) };
          ph[m.ph] = { ...(ph[m.ph] || {}), [k]: c };
          onFlowColors({ ...cur, phases: ph });
        };
        const resetPhaseBox = () => {
          const cur = flowColors || {};
          const ph = { ...(cur.phases || {}) };
          delete ph[m.ph];
          onFlowColors({ ...cur, phases: ph });
        };
        const tip = tips?.phases?.find((p) => p.phase === m.ph);
        const cand = inputCandidate(m);
        const target = inputTarget(m);
        const tipInputTask = tip?.suggested_input_task_id ? byId[tip.suggested_input_task_id] : null;
        return (
          <div key={m.ph} className="border-2 rounded-3xl p-5 bg-card" style={{ borderColor: color }} data-testid={`flow-phase-${m.ph}`}>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                {onPhaseColor ? (
                  <span title="Change this phase's color" data-testid={`flow-phase-color-${m.ph}`}>
                    <SwatchPicker value={color} onChange={(c) => onPhaseColor(m.ph, c)} title={`${m.ph} phase color`} testid={`flow-phase-swatch-${m.ph}`} />
                  </span>
                ) : (
                  <span className="h-3 w-3 rounded-full shrink-0" style={{ background: color }} />
                )}
                Phase{" "}
                {onReorderPhase ? (
                  <Popover open={numPopover === m.ph} onOpenChange={(o) => { setNumPopover(o ? m.ph : null); if (o) setLabelDraft(phaseLabels?.[m.ph] || ""); }}>
                    <PopoverTrigger asChild>
                      <button type="button" className="inline-flex items-center gap-0.5 rounded-md px-1 -mx-0.5 hover:bg-primary/10 hover:text-primary transition-colors"
                        title="Change this phase's number or give it your own label" data-testid={`flow-phase-number-${m.ph}`}>
                        {phaseLabels?.[m.ph] || m.pi + 1} <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-52 p-2.5 space-y-2.5" data-testid={`flow-phase-number-pop-${m.ph}`}>
                      <div>
                        <p className="text-xs font-bold mb-2">Move "{m.ph}" to position…</p>
                        <div className="grid grid-cols-5 gap-1">
                          {phases.map((_, k) => (
                            <button key={k} type="button" disabled={k === m.pi}
                              onClick={() => { setNumPopover(null); onReorderPhase(m.ph, k); }}
                              className={`h-8 rounded-md text-xs font-bold border transition-colors ${k === m.pi ? "bg-primary text-primary-foreground border-primary cursor-default" : "border-border hover:border-primary hover:text-primary"}`}
                              data-testid={`flow-phase-number-opt-${m.ph}-${k + 1}`}>
                              {k + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                      {onPhaseLabel && (
                        <div className="border-t border-border pt-2">
                          <p className="text-xs font-bold mb-1.5">…or use your own number / letter</p>
                          <div className="flex gap-1.5">
                            <input value={labelDraft} onChange={(e) => setLabelDraft(e.target.value.slice(0, 6))} placeholder={`e.g. A or ${m.pi + 1}b`}
                              className="flex-1 min-w-0 h-8 rounded-md border border-border bg-background px-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                              data-testid={`flow-phase-label-input-${m.ph}`} />
                            <button type="button" onClick={() => { setNumPopover(null); onPhaseLabel(m.ph, labelDraft.trim()); }}
                              className="h-8 px-2.5 rounded-md text-xs font-bold bg-primary text-primary-foreground hover:brightness-110 transition-[filter]"
                              data-testid={`flow-phase-label-save-${m.ph}`}>
                              Set
                            </button>
                          </div>
                          {phaseLabels?.[m.ph] && (
                            <button type="button" onClick={() => { setNumPopover(null); onPhaseLabel(m.ph, ""); }}
                              className="mt-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                              data-testid={`flow-phase-label-clear-${m.ph}`}>
                              Clear — back to automatic number ({m.pi + 1})
                            </button>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <>{phaseLabels?.[m.ph] || m.pi + 1}</>
                )}{" "}
                · {m.ph}
                {onFlowColors && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-primary transition-colors" title={`Box colors for ${m.ph}`} data-testid={`flow-phase-boxcolors-${m.ph}`}>
                        <Palette className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 space-y-2" data-testid={`flow-phase-boxcolors-pop-${m.ph}`}>
                      <p className="text-xs font-bold">Box colors — {m.ph}</p>
                      {Object.keys(FLOW_LABELS).map((k) => (
                        <div key={k} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">{FLOW_LABELS[k]}</span>
                          <SwatchPicker value={PC[k]} onChange={(c) => setPhaseBox(k, c)} title={`${FLOW_LABELS[k]} color for ${m.ph}`} testid={`flow-phase-color-${m.ph}-${k}`} />
                        </div>
                      ))}
                      <button type="button" onClick={resetPhaseBox} className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid={`flow-phase-boxcolors-reset-${m.ph}`}>
                        <RotateCcw className="h-3 w-3" /> Use project-wide colors
                      </button>
                    </PopoverContent>
                  </Popover>
                )}
              </h3>
              {m.items.length > 0 && (
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-extrabold tabular-nums text-muted-foreground" data-testid={`flow-pct-${m.ph}`}>{m.pct}% complete</span>
                  <span className="text-xs font-semibold text-muted-foreground" data-testid={`flow-details-${m.ph}`}>· {m.doneCount}/{m.scoredCount} tasks done</span>
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${m.health === "jeopardy" ? "bg-red-100 text-red-700" : m.health === "at_risk" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`flow-health-${m.ph}`}>
                    <span className={`h-2 w-2 rounded-full ${m.health === "jeopardy" ? "bg-red-500 animate-pulse" : m.health === "at_risk" ? "bg-amber-500" : "bg-emerald-500"}`} />
                    {m.health === "jeopardy" ? "In Jeopardy" : m.health === "at_risk" ? "At Risk" : "On Track"}
                  </span>
                  {projectId && (
                    <button type="button" onClick={() => mapPhase(m.ph)} disabled={mappingPhase === m.ph}
                      title={`Maestro maps the "${m.ph}" phase into its own process diagram on the Process page`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-primary border border-primary/40 rounded-full px-2.5 py-0.5 hover:bg-primary/10 transition-colors disabled:opacity-60"
                      data-testid={`flow-map-process-${m.ph}`}>
                      {mappingPhase === m.ph ? <Loader2 className="h-3 w-3 animate-spin" /> : <Workflow className="h-3 w-3" />} Map to process
                    </button>
                  )}
                </span>
              )}
            </div>
            {m.resources.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid={`flow-resources-${m.ph}`}>
                <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Resources:</span>
                {m.resources.map((r) => {
                  const cnt = m.resCounts[r] || 0;
                  const heat = workloadHeat(cnt);
                  return (
                  <button key={r} type="button" onClick={() => openResource(r, m.ph)} disabled={resLoading === r}
                    title={`${r} — ${cnt} open task${cnt === 1 ? "" : "s"} in this phase (${heat.label}). Click to open their card filtered to this phase.`}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-secondary hover:bg-primary/10 hover:text-primary text-foreground/80 rounded-full px-2 py-0.5 transition-colors disabled:opacity-60"
                    data-testid={`flow-resource-chip-${m.ph}-${r}`}>
                    {resLoading === r && <Loader2 className="h-3 w-3 animate-spin" />}{r}
                    <span className="flex items-center gap-1" data-testid={`flow-resource-load-${m.ph}-${r}`}>
                      <span className="h-1.5 w-7 rounded-full bg-foreground/10 overflow-hidden">
                        <span className="block h-full rounded-full transition-[width]" style={{ width: `${Math.round((cnt / m.maxRes) * 100)}%`, background: heat.bg }} />
                      </span>
                      <span className="text-[10px] font-bold" style={{ color: heat.bg }}>{cnt}</span>
                    </span>
                  </button>
                  );
                })}
                {projectId && (
                  <button type="button" onClick={() => nudgeAllPhase(m.ph)} disabled={nudgingPhase === m.ph}
                    title={`Email everyone in this phase their open "${m.ph}" tasks (with a PDF copy attached)`}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-primary-foreground bg-primary rounded-full px-2.5 py-0.5 hover:brightness-110 transition-[filter] disabled:opacity-60"
                    data-testid={`flow-nudge-all-${m.ph}`}>
                    {nudgingPhase === m.ph ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Nudge all
                  </button>
                )}
                {projectId && (
                  <button type="button" onClick={() => toggleAutoNudge(m.ph)}
                    title={effAuto.includes(m.ph) ? "Auto-nudge is ON — resources get a Monday reminder until this phase's output is delivered. Click to turn off." : "Turn on weekly auto-nudge: every Monday, resources get their open tasks for this phase until the output milestone is delivered."}
                    className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-0.5 border transition-colors ${effAuto.includes(m.ph) ? "bg-emerald-600 text-white border-emerald-600" : "text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"}`}
                    data-testid={`flow-auto-nudge-${m.ph}`}>
                    <Repeat className="h-3 w-3" /> {effAuto.includes(m.ph) ? "Auto Mon" : "Auto"}
                  </button>
                )}
              </div>
            )}

            {m.items.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No tasks in this phase yet.</p>
            ) : (
              <>
                <div className="flex flex-col lg:grid lg:grid-cols-[13rem_1.5rem_minmax(0,1fr)_3.25rem_13rem] gap-2 items-stretch">
                  {/* INPUT block */}
                  <div className="flex min-w-0">
                    {(m.inputs.length > 0 || m.ioInputs.length > 0) ? (
                      <div className="rounded-md p-3 text-white flex flex-col justify-center gap-1.5 w-full" style={{ background: PC.input }} data-testid={`flow-input-${m.ph}`}>
                        <span className="text-[11px] font-bold uppercase tracking-wide">Input{m.inputs.length > 0 ? ": Dependencies" : ""}<br /><span className="font-normal normal-case opacity-80">(what is needed to start)</span></span>
                        {m.inputText && <span className="text-[11px] italic opacity-90">{m.inputText}</span>}
                        <span className="flex flex-col gap-1 mt-1">
                          {m.ioInputs.map((p) => (
                            <span key={p.id} className="group flex items-center gap-1 w-full">
                              <button type="button" onClick={() => onOpenTask(p)} className="flex-1 min-w-0 text-left text-xs bg-white/15 hover:bg-white/25 rounded px-1.5 py-1 inline-flex items-start gap-1.5" data-testid={`flow-io-input-${p.id}`}>
                                {p.is_milestone && <Flag className="h-3 w-3 mt-0.5 shrink-0" />}<span className="font-mono text-[10px] font-bold opacity-80 shrink-0">{wbsIds[p.id]}</span><span className="leading-tight">{p.title}</span>
                              </button>
                              {onSetFlowRole && <IoRolePicker t={p} light />}
                            </span>
                          ))}
                          {m.inputs.map((p) => (
                            <button key={p.id} type="button" onClick={() => onOpenTask(p)} className="text-left text-xs bg-white/15 hover:bg-white/25 rounded px-1.5 py-1 inline-flex items-start gap-1.5">
                              {p.is_milestone && <Flag className="h-3 w-3 mt-0.5 shrink-0" />}<span className="font-mono text-[10px] font-bold opacity-80 shrink-0">{wbsIds[p.id]}</span><span className="leading-tight">{p.title}</span>
                            </button>
                          ))}
                        </span>
                      </div>
                    ) : m.pi === 0 ? (
                      <div className="rounded-md p-3 text-white text-xs flex flex-col justify-center gap-1 w-full" style={{ background: PC.input }} data-testid={`flow-input-${m.ph}`}>
                        <span className="text-[11px] font-bold uppercase tracking-wide mb-1">Input</span>
                        {m.inputText ? <span className="italic opacity-90">{m.inputText}</span> : <span>Project kickoff — first phase, no upstream input required.</span>}
                      </div>
                    ) : (
                      <div className="rounded-md p-3 border-2 border-dashed border-amber-400 bg-amber-50 flex flex-col justify-center gap-1.5 w-full" data-testid={`flow-input-missing-${m.ph}`}>
                        <span className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" /> No input — nothing feeds this phase.</span>
                        {cand && target && (
                          <button type="button" onClick={() => onLinkInput(target.id, cand.id)}
                            className="inline-flex items-center gap-1 text-left text-xs font-bold text-amber-900 bg-amber-200/70 hover:bg-amber-200 rounded px-2 py-1" data-testid={`flow-fix-input-${m.ph}`}>
                            <Link2 className="h-3 w-3 shrink-0" /> Link "{cand.title}" as input
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* brace */}
                  <div className="hidden lg:flex items-center text-6xl font-thin select-none shrink-0" style={{ color: "#7db3dd", fontFamily: "serif" }}>{"}"}</div>

                  {/* MIDDLE: summary + tasks + milestones */}
                  <div className="flex-1 min-w-[260px] flex flex-col gap-2">
                    <div className="rounded px-3 py-1.5 text-white text-xs font-semibold text-center" style={{ background: `linear-gradient(90deg,${PC.summary},${PC.summary}88)` }} data-testid={`flow-summary-${m.ph}`}>
                      Summary (goal): {m.summary?.title || `Deliver ${m.ph}`}
                    </div>
                    <div className="flex flex-col md:flex-row gap-3 items-start">
                      <div className="flex-1 w-full min-w-0 flex flex-col gap-1">
                        {m.workTasks.map((t) => {
                          const h = taskHealth(t, today);
                          const hc = HEALTH_COLORS[h];
                          return (
                          <span key={t.id} className="group flex items-center gap-1 w-full">
                          <button type="button" onClick={() => onOpenTask(t)}
                            className="flex-1 min-w-0 flex items-center gap-2 text-xs rounded px-2 py-1 text-slate-900 hover:brightness-95"
                            style={{ background: `linear-gradient(90deg,${hc}30,${hc}12)`, border: `1.5px solid ${hc}` }}
                            data-health={h}
                            data-testid={`flow-task-${t.id}`}>
                            {isDoneT(t) ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" data-testid={`flow-done-${t.id}`} /> : <span className="h-2 w-2 rounded-full shrink-0" style={{ background: hc }} />}
                            <span className="font-mono text-[10px] font-bold opacity-70 shrink-0">{wbsIds[t.id]}</span>
                            <span className="truncate">{t.title}</span>
                            <span className={`text-[10px] font-bold shrink-0 ml-auto tabular-nums ${isDoneT(t) ? "text-emerald-700" : "opacity-70"}`} data-testid={`flow-task-pct-${t.id}`}>{isDoneT(t) ? 100 : (t.percent_complete || 0)}%</span>
                            {t.end_date && <span className="text-[10px] opacity-70 shrink-0">{fmtD(t.end_date)}</span>}
                          </button>
                          {onSetFlowRole && <IoRolePicker t={t} />}
                          </span>
                          );
                        })}
                        {m.workTasks.length === 0 && <p className="text-[11px] text-muted-foreground italic px-1">No tasks yet.</p>}
                      </div>
                      {m.midMilestones.length > 0 && (
                        <div className="flex items-center gap-2 shrink-0 min-w-0 md:max-w-[45%]">
                          <div className="hidden sm:flex items-center text-5xl font-thin select-none" style={{ color: "#7db3dd", fontFamily: "serif" }}>{"}"}</div>
                          <div className="flex flex-col gap-1 min-w-0">
                            {m.midMilestones.map((ms) => (
                              <span key={ms.id} className="group flex items-center gap-1 min-w-0">
                              <button type="button" onClick={() => onOpenTask(ms)}
                                className="flex-1 min-w-0 flex items-center gap-1.5 text-xs rounded px-2 py-1 text-white hover:brightness-110" style={{ background: PC.milestone }} data-testid={`flow-ms-${ms.id}`}>
                                {isDoneT(ms) ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-300" /> : <Flag className="h-3 w-3 shrink-0" />}<span className="font-mono text-[10px] font-bold opacity-80 shrink-0">{wbsIds[ms.id]}</span><span className="truncate">{ms.title}</span><span className="text-[10px] font-bold opacity-80 shrink-0">{isDoneT(ms) ? "100%" : `${ms.percent_complete || 0}%`}</span>
                              </button>
                              {onSetFlowRole && <IoRolePicker t={ms} />}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* arrow */}
                  <div className="hidden lg:flex items-center justify-center"><GreenArrow /></div>

                  {/* OUTPUT block */}
                  <div className="flex flex-col gap-2 min-w-0">
                    {m.output ? (
                      <>
                        <div className="relative group flex-1 flex">
                        <button type="button" onClick={() => onOpenTask(m.output)}
                          className="rounded-md p-3 text-white text-xs font-semibold text-left w-full flex flex-col justify-center gap-1 hover:brightness-110"
                          style={{ background: `linear-gradient(135deg,${PC.output},#5cb86a)` }} data-testid={`flow-output-${m.ph}`}>
                          <span className="text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1.5">{isDoneT(m.output) ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-200" /> : <Flag className="h-3.5 w-3.5" />} Output: {m.output.is_milestone ? "Milestone" : "Task"}{isDoneT(m.output) ? " — done" : ""}</span>
                          <span className="text-sm leading-tight">{m.output.title}</span>
                          {m.extraOutputs.map((o) => (
                            <span key={o.id} className="text-[11px] opacity-90 inline-flex items-center gap-1" data-testid={`flow-extra-output-${o.id}`}>
                              {o.is_milestone ? <Flag className="h-3 w-3 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />}{o.title}
                            </span>
                          ))}
                          <span className="opacity-75 font-normal">(what was promised)</span>
                        </button>
                        {onSetFlowRole && <span className="absolute top-1.5 right-1.5"><IoRolePicker t={m.output} light /></span>}
                        </div>
                        <div className={`rounded-md border text-xs font-bold text-center px-2 py-2 ${m.delivery && m.delivery < today && m.pct < 100 ? "border-red-500 bg-red-600 text-white animate-pulse" : "border-emerald-400 bg-emerald-50 text-emerald-800"}`} data-testid={`flow-delivery-${m.ph}`}>
                          {m.delivery && m.delivery < today && m.pct < 100 ? "⚠ Past due — " : ""}Delivery: {fmtD(m.output.end_date || m.output.start_date) || "No date yet"}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-md p-3 border-2 border-dashed border-red-400 bg-red-50 flex flex-col justify-center gap-1.5 flex-1" data-testid={`flow-output-missing-${m.ph}`}>
                        <span className="text-xs font-semibold text-red-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-600 shrink-0" /> No output milestone — what does this phase promise?</span>
                        <button type="button" onClick={() => onCreateMilestone(m.ph, tip?.suggested_output_milestone || `${m.ph} delivered`)}
                          className="inline-flex items-center gap-1 text-left text-xs font-bold text-red-900 bg-red-200/70 hover:bg-red-200 rounded px-2 py-1" data-testid={`flow-add-output-${m.ph}`}>
                          <Plus className="h-3 w-3 shrink-0" /> Add "{tip?.suggested_output_milestone || `${m.ph} delivered`}"
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {(tip?.input_tip || tip?.output_tip) && (
                  <div className="mt-3 space-y-1">
                    {tip?.input_tip && (
                      <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 flex items-start gap-1.5"><Sparkles className="h-3 w-3 mt-0.5 shrink-0" /><span className="flex-1">{tip.input_tip}</span>
                        {tipInputTask && target && !m.inputs.some((x) => x.id === tipInputTask.id) && (
                          <button type="button" onClick={() => onLinkInput(target.id, tipInputTask.id)} className="font-bold underline ml-1 shrink-0" data-testid={`flow-maestro-input-${m.ph}`}>Apply</button>
                        )}
                        <TipActionBtn k={`${m.ph}-in`} tip={tip.input_tip} phase={m.ph} />
                      </p>
                    )}
                    {tip?.output_tip && (
                      <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 flex items-start gap-1.5"><Sparkles className="h-3 w-3 mt-0.5 shrink-0" /><span className="flex-1">{tip.output_tip}</span>
                        <TipActionBtn k={`${m.ph}-out`} tip={tip.output_tip} phase={m.ph} />
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      <Dialog open={!!resCard} onOpenChange={(v) => { if (!v) { setResCard(null); setResPhase(null); } }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="flow-resource-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Resource card</DialogTitle>
          </DialogHeader>
          {resCard && <PersonCard card={resCard} defaultOpen phaseFilter={resPhase} onClearPhaseFilter={() => setResPhase(null)} onChanged={() => openResource(resCard.person, resPhase)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
